import { supabase } from '../clients/supabaseClient';
import type { Special, SpecialSchedule, ProductItem, CategoryItem, StorageImageItem } from '../types';


const SEARCH_RESULT_LIMIT = 20;
export const IMAGE_PAGE_SIZE = 24;
export const SEARCH_DEBOUNCE_MS = 250;
export const MIN_SEARCH_CHARS = 2;
export const BUSINESS_TIME_ZONE = 'Africa/Johannesburg';

export async function fetchSpecialsFromDb(): Promise<{ specials: Special[]; schedules: SpecialSchedule[] }> {
    const [specialsRes, schedulesRes] = await Promise.all([
        supabase.from('specials').select('*').order('created_at', { ascending: false }),
        supabase.from('special_schedules').select('*').order('created_at', { ascending: false }),
    ]);
    if (specialsRes.error) throw specialsRes.error;
    if (schedulesRes.error) throw schedulesRes.error;
    return {
        specials:  (specialsRes.data  ?? []) as Special[],
        schedules: (schedulesRes.data ?? []) as SpecialSchedule[],
    };
}

export async function saveSpecialInDb(
    payload: Omit<Special, 'id' | 'sort_order' | 'created_at'>,
    editingId?: string
): Promise<string> {
    const { data, error } = editingId
        ? await supabase.from('specials').update(payload).eq('id', editingId).select('id').single()
        : await supabase.from('specials').insert(payload).select('id').single();
    if (error) throw error;
    return data.id;
}

export async function deleteSpecialFromDb(id: string): Promise<void> {
    const { error } = await supabase.from('specials').delete().eq('id', id);
    if (error) throw error;
}

export async function saveScheduleInDb(
    schedulePayload: object,
    specialId: string,
    existingSchedules: SpecialSchedule[]
): Promise<void> {
    const existing = existingSchedules.find(s => s.special_id === specialId);
    if (existing) {
        const { error } = await supabase.from('special_schedules').update(schedulePayload).eq('id', existing.id);
        if (error) throw error;
    } else {
        const { error } = await supabase.from('special_schedules').insert(schedulePayload);
        if (error) throw error;
    }
}

export async function deleteSchedulesForSpecialInDb(specialId: string): Promise<void> {
    const { error } = await supabase.from('special_schedules').delete().eq('special_id', specialId);
    if (error) throw error;
}

export async function fetchProductsFromDb(searchQuery = ''): Promise<ProductItem[]> {
    if (searchQuery.trim().length >= MIN_SEARCH_CHARS) {
        const { data, error } = await supabase
            .from('product_inventory_view')
            .select('id, name, image_url')
            .textSearch('search_fts', searchQuery.trim(), { type: 'websearch', config: 'simple' })
            .limit(SEARCH_RESULT_LIMIT);
        if (error) throw error;
        return (data ?? []) as ProductItem[];
    }
    const { data, error } = await supabase
        .from('product_inventory_view')
        .select('id, name, image_url')
        .order('name')
        .limit(SEARCH_RESULT_LIMIT);
    if (error) throw error;
    return (data ?? []) as ProductItem[];
}

export async function fetchCategoriesFromDb(searchQuery = ''): Promise<CategoryItem[]> {
    let q = supabase.from('categories').select('id, name').order('name').limit(SEARCH_RESULT_LIMIT);
    if (searchQuery.trim().length >= MIN_SEARCH_CHARS) {
        q = q.ilike('name', `%${searchQuery.trim()}%`);
    }
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as CategoryItem[];
}

export async function fetchBucketImagesFromDb(offset: number): Promise<StorageImageItem[]> {
    const { data, error } = await supabase.storage
        .from('product_images')
        .list('specials', { limit: IMAGE_PAGE_SIZE, offset, sortBy: { column: 'name', order: 'asc' } });
    if (error) throw error;
    return (data ?? [])
        .filter(f => !!f.name)
        .map(f => {
            const path = `specials/${f.name}`;
            const { data: pub } = supabase.storage.from('product_images').getPublicUrl(path);
            return { name: f.name, path, publicUrl: pub.publicUrl };
        });
}

export async function uploadSpecialImageToDb(file: File): Promise<string> {
    const cleanName = file.name.replace(/\s+/g, '-').toLowerCase();
    const filePath  = `specials/${Date.now()}-${cleanName}`;
    const { error } = await supabase.storage.from('product_images').upload(filePath, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('product_images').getPublicUrl(filePath);
    return data.publicUrl;
}

export const formatDateTimeInBusinessTZ = (value?: string | null): string => {
    if (!value) return '—';
    return new Intl.DateTimeFormat('en-ZA', {
        dateStyle: 'medium', timeStyle: 'short', timeZone: BUSINESS_TIME_ZONE,
    }).format(new Date(value));
};

export const businessDatetimeLocalToISO = (value: string): string => {
    const [datePart, timePart] = value.split('T');
    if (!datePart || !timePart) return value;
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute]     = timePart.split(':').map(Number);
    const utcGuess  = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: BUSINESS_TIME_ZONE, year: 'numeric', month: '2-digit',
        day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
    const parts = formatter.formatToParts(utcGuess);
    const map   = Object.fromEntries(parts.map(p => [p.type, p.value]));
    const asBusinessUtc = Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day), Number(map.hour), Number(map.minute), Number(map.second));
    const offsetMs = asBusinessUtc - utcGuess.getTime();
    return new Date(utcGuess.getTime() - offsetMs).toISOString();
};